import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, Button, IconButton, Divider, Alert,
  Skeleton, Snackbar, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableRow, Stack,
  TextField, FormControl, InputLabel, Select, MenuItem, ListSubheader,
} from '@mui/material';
import {
  ArrowBack, Forest, Edit, Delete, Park, ChevronRight, Map as MapIcon,
  Straighten, Event, Star, AssignmentTurnedIn, Add, PlayArrow, CheckCircle,
} from '@mui/icons-material';
import api, { measuresAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { MEASURE_TYPE_GROUPS, getMeasureBeschreibung } from '../data/treeFieldOptions';
import FlaecheFormDialog, {
  FlaecheForm, emptyFlaecheForm, flaecheToForm, formToPayload,
} from '../components/FlaecheFormDialog';

const ZUSTAND_OPTIONS = ['gut', 'mittel', 'schlecht'];

function getFlaecheZustandColor(z: string | null): string {
  switch ((z || '').toLowerCase()) {
    case 'gut': return '#2e7d32';
    case 'mittel': return '#f9a825';
    case 'schlecht': return '#c62828';
    default: return '#78909c';
  }
}

function statusColor(s: string): string {
  switch (s) {
    case 'in Arbeit': return '#e65100';
    case 'erledigt': return '#2e7d32';
    case 'abgebrochen': return '#9e9e9e';
    default: return '#1565c0'; // offen
  }
}

function formatFlaeche(qm: number | null): string {
  if (qm == null) return 'kein Polygon';
  const n = Number(qm);
  if (n >= 10000) return `${(n / 10000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} ha`;
  return `${n.toLocaleString('de-DE', { maximumFractionDigits: 0 })} m²`;
}

function fmtDate(d: string | null): string {
  return d ? new Date(d).toLocaleDateString('de-DE') : '–';
}

function StatBox({ value, label, color }: { value: React.ReactNode; label: string; color?: string }) {
  return (
    <Box sx={{ flex: 1, textAlign: 'center', py: 1.2, px: 1, borderRadius: 2, bgcolor: '#f7f9f7', minWidth: 90 }}>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.1, color: color || 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
    </Box>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell sx={{ border: 0, py: 0.6, color: 'text.secondary', fontSize: '0.8rem', width: '40%', verticalAlign: 'top' }}>{label}</TableCell>
      <TableCell sx={{ border: 0, py: 0.6, fontSize: '0.85rem', fontWeight: 500 }}>{value || '–'}</TableCell>
    </TableRow>
  );
}

export default function FlaecheDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEdit, isAdmin, user } = useAuth();

  const [flaeche, setFlaeche] = useState<any>(null);
  const [measures, setMeasures] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  // Bearbeiten
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<FlaecheForm>({ ...emptyFlaecheForm });
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Kontrolle durchführen
  const [controlOpen, setControlOpen] = useState(false);
  const [controlForm, setControlForm] = useState({ datum: new Date().toISOString().split('T')[0], zustand: '', bemerkung: '' });
  const [savingControl, setSavingControl] = useState(false);

  // Maßnahme anlegen
  const [measureOpen, setMeasureOpen] = useState(false);
  const [measureForm, setMeasureForm] = useState({ measure_type: '', description: '', planned_date: '', status: 'offen', assigned_to: '' });
  const [savingMeasure, setSavingMeasure] = useState(false);

  // Hinweis nach „schlecht"-Kontrolle: direkt Maßnahme anlegen?
  const [promptMeasure, setPromptMeasure] = useState(false);

  const loadFlaeche = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/flaechen/${id}`);
      setFlaeche(res.data);
      setError('');
    } catch {
      setError('Fläche konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadMeasures = useCallback(async () => {
    try {
      const res = await api.get('/measures', { params: { flaeche_id: id } });
      setMeasures(res.data || []);
    } catch { setMeasures([]); }
  }, [id]);

  useEffect(() => { loadFlaeche(); loadMeasures(); }, [loadFlaeche, loadMeasures]);
  useEffect(() => { api.get('/users').then(res => setUsers(res.data || [])).catch(() => {}); }, []);

  const openEdit = () => { if (flaeche) { setForm(flaecheToForm(flaeche)); setEditOpen(true); } };

  const handleSave = async () => {
    if (!form.name.trim()) { setSnackbar('Name erforderlich'); return; }
    setSaving(true);
    try {
      await api.put(`/flaechen/${id}`, formToPayload(form));
      setSnackbar('Fläche aktualisiert');
      setEditOpen(false);
      loadFlaeche();
    } catch {
      setSnackbar('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/flaechen/${id}`);
      navigate('/flaechen');
    } catch {
      setSnackbar('Fehler beim Löschen');
      setDeleting(false);
    }
  };

  const handleSaveControl = async () => {
    setSavingControl(true);
    try {
      await api.post(`/flaechen/${id}/kontrollen`, {
        datum: controlForm.datum,
        zustand: controlForm.zustand || null,
        bemerkung: controlForm.bemerkung || null,
      });
      setSnackbar('Kontrolle erfasst');
      setControlOpen(false);
      const wasBad = controlForm.zustand === 'schlecht';
      const note = controlForm.bemerkung;
      setControlForm({ datum: new Date().toISOString().split('T')[0], zustand: '', bemerkung: '' });
      loadFlaeche();
      // Bei schlechtem Zustand: direkt fragen, ob eine Maßnahme angelegt werden soll
      if (wasBad) {
        setMeasureForm(prev => ({ ...prev, description: note || prev.description }));
        setPromptMeasure(true);
      }
    } catch {
      setSnackbar('Fehler beim Speichern der Kontrolle');
    } finally {
      setSavingControl(false);
    }
  };

  const handleCreateMeasure = async () => {
    if (!measureForm.measure_type) return;
    setSavingMeasure(true);
    try {
      await measuresAPI.create({
        flaeche_id: id,
        measure_type: measureForm.measure_type,
        description: measureForm.description || null,
        planned_date: measureForm.planned_date || null,
        status: measureForm.status,
        assigned_to: measureForm.assigned_to || null,
      });
      setSnackbar('Maßnahme angelegt');
      setMeasureOpen(false);
      setMeasureForm({ measure_type: '', description: '', planned_date: '', status: 'offen', assigned_to: '' });
      loadMeasures();
      loadFlaeche();
    } catch (e: any) {
      setSnackbar(e?.response?.data?.error || 'Fehler beim Anlegen der Maßnahme');
    } finally {
      setSavingMeasure(false);
    }
  };

  const handleMeasureStatus = async (mid: string, status: string) => {
    try {
      const completed_date = status === 'erledigt' ? new Date().toISOString().split('T')[0] : undefined;
      await measuresAPI.update(mid, { status, completed_date });
      setSnackbar('Status aktualisiert');
      loadMeasures();
    } catch { setSnackbar('Fehler beim Aktualisieren'); }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} />
      </Box>
    );
  }

  if (error || !flaeche) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/flaechen')} sx={{ mb: 2 }}>Zurück</Button>
        <Alert severity="error">{error || 'Fläche nicht gefunden'}</Alert>
      </Box>
    );
  }

  const z = flaeche.zaehlung || { erfasst: 0, geschaetzt: 0, gesamt: 0, offene_massnahmen: 0 };
  const arten: string[] = flaeche.arten_gesamt || flaeche.arten || [];
  const baeume: any[] = flaeche.baeume || [];
  const kontrollen: any[] = flaeche.kontrollen || [];
  const accent = getFlaecheZustandColor(flaeche.zustand);
  const offeneMassnahmen = measures.filter(m => m.status === 'offen' || m.status === 'in Arbeit');

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/flaechen')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }} />
        {canEdit && <Button startIcon={<AssignmentTurnedIn />} variant="contained" onClick={() => setControlOpen(true)} sx={{ textTransform: 'none', bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>Kontrolle durchführen</Button>}
        {canEdit && <Button startIcon={<Edit />} onClick={openEdit} sx={{ textTransform: 'none' }}>Bearbeiten</Button>}
        {isAdmin && <Button startIcon={<Delete />} color="error" onClick={() => setDeleteOpen(true)} sx={{ textTransform: 'none' }}>Löschen</Button>}
      </Box>

      {/* Titelkarte */}
      <Paper sx={{ p: 2.5, borderRadius: 2, mb: 2, borderLeft: '5px solid', borderLeftColor: accent }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '50%', bgcolor: accent + '18',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Forest sx={{ color: accent, fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} noWrap>{flaeche.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.3 }}>
              {flaeche.typ && <Chip label={flaeche.typ} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />}
              <Chip icon={<Straighten sx={{ fontSize: '14px !important' }} />} label={formatFlaeche(flaeche.flaeche_qm)} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              {flaeche.zustand && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: accent }} />
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{flaeche.zustand}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <StatBox value={z.erfasst} label="erfasst" color="#1565c0" />
          <StatBox value={`+${z.geschaetzt}`} label="geschätzt" />
          <StatBox value={`ca. ${z.gesamt}`} label="Bäume gesamt" color={accent} />
          {z.offene_massnahmen > 0 && <StatBox value={z.offene_massnahmen} label="offene Baum-Maßn." color="#e65100" />}
        </Stack>

        {arten.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Arten</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.4 }}>
              {arten.map(a => <Chip key={a} label={a} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} />)}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Polygon / Karte */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <MapIcon sx={{ color: 'text.disabled' }} />
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography variant="body2" fontWeight={600}>
            {flaeche.geometry ? 'Polygon gezeichnet' : 'Noch kein Polygon gezeichnet'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {flaeche.geometry ? 'Die Fläche ist auf der Karte sichtbar.' : 'Zeichne die Fläche auf der Karte ein, um Größe und enthaltene Bäume zu erfassen.'}
          </Typography>
        </Box>
        {flaeche.geometry && (
          <Button size="small" startIcon={<MapIcon />} onClick={() => navigate(`/map?flaeche=${id}`)} sx={{ textTransform: 'none' }}>
            Zur Karte
          </Button>
        )}
        {canEdit && (
          <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => navigate(`/map?flaeche=${id}&draw=1`)} sx={{ textTransform: 'none' }}>
            {flaeche.geometry ? 'Polygon bearbeiten' : 'Polygon zeichnen'}
          </Button>
        )}
      </Paper>

      {/* Maßnahmen */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
            Maßnahmen {measures.length > 0 && `(${offeneMassnahmen.length} offen / ${measures.length} gesamt)`}
          </Typography>
          {canEdit && (
            <Button size="small" startIcon={<Add />} onClick={() => setMeasureOpen(true)} sx={{ textTransform: 'none' }}>
              Maßnahme
            </Button>
          )}
        </Box>
        {measures.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Keine Maßnahmen für diese Fläche.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {measures.map(m => (
              <Box key={m.id} sx={{
                p: 1, borderRadius: 1.5, border: '1px solid', borderColor: '#eee',
                borderLeft: '3px solid', borderLeftColor: statusColor(m.status),
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
                  <Chip label={m.status} size="small" sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: statusColor(m.status) + '18', color: statusColor(m.status) }} />
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.84rem' }}>{m.measure_type}</Typography>
                  <Box sx={{ flex: 1 }} />
                  {canEdit && m.status !== 'erledigt' && m.status !== 'abgebrochen' && (
                    <>
                      {m.status !== 'in Arbeit' && (
                        <Button size="small" startIcon={<PlayArrow sx={{ fontSize: 16 }} />} onClick={() => handleMeasureStatus(m.id, 'in Arbeit')} sx={{ textTransform: 'none', minWidth: 0, fontSize: '0.72rem' }}>In Arbeit</Button>
                      )}
                      <Button size="small" color="success" startIcon={<CheckCircle sx={{ fontSize: 16 }} />} onClick={() => handleMeasureStatus(m.id, 'erledigt')} sx={{ textTransform: 'none', minWidth: 0, fontSize: '0.72rem' }}>Erledigt</Button>
                    </>
                  )}
                </Box>
                {m.description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>{m.description}</Typography>}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.3, flexWrap: 'wrap' }}>
                  {m.planned_date && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Geplant: {fmtDate(m.planned_date)}</Typography>}
                  {m.assigned_to_name && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Bearbeiter: {m.assigned_to_name}{m.assigned_to === user?.id ? ' (du)' : ''}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Datenblatt */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Stammdaten</Typography>
        <Table size="small">
          <TableBody>
            <Row label="Eigentümer" value={flaeche.eigentuemer} />
            <Row label="Gemeinde" value={flaeche.gemeinde} />
            <Row label="Ort" value={flaeche.ort} />
            <Row label="Straße / Lage" value={flaeche.strasse} />
            <Row label="Kontroll-Intervall" value={flaeche.kontroll_intervall} />
            <Row label="Letzte Kontrolle" value={fmtDate(flaeche.letzte_kontrolle)} />
            <Row label="Nächste Kontrolle" value={fmtDate(flaeche.naechste_kontrolle)} />
            <Row label="Bemerkung" value={flaeche.bemerkung} />
          </TableBody>
        </Table>
      </Paper>

      {/* Enthaltene Einzelbäume */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Erfasste Bäume in dieser Fläche {baeume.length > 0 && `(${baeume.length})`}
        </Typography>
        {baeume.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Keine einzeln erfassten Bäume im Polygon. {flaeche.geometry ? '' : '(Polygon fehlt noch.)'}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {baeume.map(b => (
              <Box key={b.id} onClick={() => navigate(`/trees/${b.id}`)} sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5,
                cursor: 'pointer', border: '1px solid', borderColor: '#eee',
                opacity: b.gefaellt ? 0.55 : 1, '&:hover': { bgcolor: '#f5f5f5' },
              }}>
                <Park sx={{ fontSize: 18, color: '#2e7d32' }} />
                <Typography fontWeight={700} sx={{ fontSize: '0.82rem' }}>{b.baumnummer}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', flex: 1 }} noWrap>
                  {b.baumart || 'Unbekannt'}
                </Typography>
                {Number(b.offene_massnahmen) > 0 && (
                  <Chip icon={<Star sx={{ fontSize: '13px !important' }} />} label={b.offene_massnahmen} size="small"
                    sx={{ height: 20, fontSize: '0.66rem', bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, '& .MuiChip-icon': { color: '#1565c0' } }} />
                )}
                {b.gefaellt && <Chip label="Gefällt" size="small" sx={{ height: 20, fontSize: '0.66rem' }} />}
                <ChevronRight sx={{ color: '#ccc', fontSize: 18 }} />
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Kontrollhistorie */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Kontrollhistorie {kontrollen.length > 0 && `(${kontrollen.length})`}
        </Typography>
        {kontrollen.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Noch keine Kontrollen erfasst.</Typography>
        ) : (
          kontrollen.map((k, i) => (
            <Box key={k.id}>
              {i > 0 && <Divider sx={{ my: 1 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Event sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{fmtDate(k.datum)}</Typography>
                {k.zustand && <Chip label={k.zustand} size="small" sx={{ height: 18, fontSize: '0.66rem' }} />}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{k.kontrolleur || ''}</Typography>
              </Box>
              {k.bemerkung && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', ml: 3 }}>{k.bemerkung}</Typography>}
            </Box>
          ))
        )}
      </Paper>

      {/* Edit-Dialog */}
      <FlaecheFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={handleSave}
        form={form}
        setForm={setForm}
        saving={saving}
        isEdit
      />

      {/* Kontrolle durchführen */}
      <Dialog open={controlOpen} onClose={() => !savingControl && setControlOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#2e7d32', color: 'white', py: 1.5 }}>Kontrolle durchführen</DialogTitle>
        <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Datum" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={controlForm.datum} onChange={e => setControlForm({ ...controlForm, datum: e.target.value })} />
          <FormControl fullWidth size="small">
            <InputLabel>Zustand</InputLabel>
            <Select label="Zustand" value={controlForm.zustand} onChange={e => setControlForm({ ...controlForm, zustand: e.target.value })}>
              <MenuItem value=""><em>unverändert</em></MenuItem>
              {ZUSTAND_OPTIONS.map(z => <MenuItem key={z} value={z}>{z}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Bemerkung" size="small" multiline minRows={3}
            placeholder="Beobachtungen, Handlungsbedarf, …"
            value={controlForm.bemerkung} onChange={e => setControlForm({ ...controlForm, bemerkung: e.target.value })} />
          <Typography variant="caption" color="text.secondary">
            Datum wird als „letzte Kontrolle" gespeichert, die nächste Kontrolle aus dem Intervall ({flaeche.kontroll_intervall}) berechnet.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 1.5 }}>
          <Button onClick={() => setControlOpen(false)} disabled={savingControl}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveControl} disabled={savingControl} sx={{ bgcolor: '#2e7d32' }}>
            {savingControl ? <CircularProgress size={20} /> : 'Kontrolle speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hinweis nach schlechter Kontrolle */}
      <Dialog open={promptMeasure} onClose={() => setPromptMeasure(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Maßnahme anlegen?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Die Fläche wurde als „schlecht" bewertet. Möchtest du direkt eine Maßnahme dazu anlegen?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptMeasure(false)}>Später</Button>
          <Button variant="contained" sx={{ bgcolor: '#4a7c59', '&:hover': { bgcolor: '#3a6447' } }}
            onClick={() => { setPromptMeasure(false); setMeasureOpen(true); }}>
            Maßnahme anlegen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Maßnahme anlegen */}
      <Dialog open={measureOpen} onClose={() => !savingMeasure && setMeasureOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4a7c59', color: 'white', py: 1.5 }}>Maßnahme für Fläche anlegen</DialogTitle>
        <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Maßnahmentyp *</InputLabel>
            <Select
              value={measureForm.measure_type}
              label="Maßnahmentyp *"
              onChange={e => {
                const newType = e.target.value;
                const prevBeschr = getMeasureBeschreibung(measureForm.measure_type);
                const newBeschr = getMeasureBeschreibung(newType);
                const cur = measureForm.description || '';
                const shouldFill = cur.trim() === '' || cur.trim() === prevBeschr.trim();
                setMeasureForm({ ...measureForm, measure_type: newType, description: shouldFill ? newBeschr : cur });
              }}
            >
              {MEASURE_TYPE_GROUPS.flatMap((group: any) => [
                <ListSubheader key={`hdr-${group.label}`} sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', lineHeight: '28px', bgcolor: 'background.paper' }}>
                  {group.label}
                </ListSubheader>,
                ...group.items.map((t: any) => (
                  <MenuItem key={t.value} value={t.value} sx={{ pl: 3, py: 0.7 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.value}</Typography>
                      {t.beschreibung !== t.value && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>{t.beschreibung}</Typography>
                      )}
                    </Box>
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
          <TextField label="Beschreibung" size="small" multiline rows={2}
            value={measureForm.description} onChange={e => setMeasureForm({ ...measureForm, description: e.target.value })} />
          <TextField label="Geplantes Datum" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={measureForm.planned_date} onChange={e => setMeasureForm({ ...measureForm, planned_date: e.target.value })} />
          <FormControl fullWidth size="small">
            <InputLabel>Bearbeiter</InputLabel>
            <Select label="Bearbeiter" value={measureForm.assigned_to} displayEmpty
              onChange={e => setMeasureForm({ ...measureForm, assigned_to: e.target.value })}>
              <MenuItem value=""><em>Niemand</em></MenuItem>
              {user && <MenuItem value={user.id}>Mir selbst (du)</MenuItem>}
              {users.filter((u: any) => u.id !== user?.id).map((u: any) => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={measureForm.status} onChange={e => setMeasureForm({ ...measureForm, status: e.target.value })}>
              <MenuItem value="offen">Offen</MenuItem>
              <MenuItem value="in Arbeit">In Arbeit</MenuItem>
              <MenuItem value="erledigt">Erledigt</MenuItem>
              <MenuItem value="abgebrochen">Abgebrochen</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 1.5 }}>
          <Button onClick={() => setMeasureOpen(false)} disabled={savingMeasure}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreateMeasure} disabled={savingMeasure || !measureForm.measure_type} sx={{ bgcolor: '#4a7c59' }}>
            {savingMeasure ? <CircularProgress size={20} /> : 'Maßnahme speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Löschen */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Fläche löschen</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Die Fläche wird mit Kontrollen, Maßnahmen und Fotos gelöscht. Erfasste Einzelbäume bleiben erhalten.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar('')}
        message={snackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
